import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { style_border_default } from '../../../../../shared/constants/stylesValues';
import AppliedApplicationTemplateContainer from '../../../../../shared/state/containers/AppliedApplicationTemplateContainer';
import { breakpoint_small, breakpoint_macbook_small, breakpoint_large } from '../../../../../shared/constants/breakpoints';
import { ApplicationModelWithPhoneNumberAndStatus, ApplicationType } from './ApplicationDashboard';
import ApplicationAnalyticsFacetContainer from '../../../../../shared/state/containers/ApplicationAnalyticsFacetContainer';
import ConditionalTooltipWrapper from '../../../../../shared/components/general/ConditionalTooltipWrapper';
import TooltipWrapper from '../../../../../shared/components/general/TooltipWrapper';
import { color_colors_decline, color_colors_growth, color_shades_light, dark_grey_blue, color_shades_lightest, color_variants_ocean_light_opaque, color_shades_darker, voicify_spark_lightest, voicify_spark_medium, voicify_spark_darker, color_defaults_white, color_colors_gold, color_variants_gold_light, premium_gold, premium_gold_lightest, premium_gold_shadow, color_shades_darkest } from '../../../../../shared/constants/colors';
import SparkDeploymentContainer from '../../../../../shared/hooks/SparkDeploymentContainer';
import { aiAnsweringCommonAppName, aiDentalSchedulingCommonAppName, aiOrderingCommonAppName } from '../../../../../shared/constants/sparkConstants/moduleConsts';
import moment from 'moment';
import { DATE_FORMAT } from '../../../../../shared/models/analytics/AnalyticsDateFormat';
import AnalyticsInterval from '../../../../../shared/models/analytics/AnalyticsInterval';
import UserAvatar from '../../../../../shared/components/general/UserAvatar';

const assistantImage = require('../../../../../shared/content/images/assistant-borderless.svg');
const sharedImage = require('../../../../../shared/content/images/spark/shared.svg');
const formCompleteImage = require('../../../../../shared/content/images/task-checklist-check.svg');
const formIncompleteImage = require('../../../../../shared/content/images/task-checklist-remove.svg');
const editImage = require("../../../../../shared/content/images/custom-assistant-deployment/edit.svg");
const analyticsIcon = require('../../../../../shared/content/images/tab-icons/analytics-blue.svg');
const copyIcon = require('../../../../../shared/content/images/copy-text.svg');

interface ApplicationItemProps {
    app: ApplicationModelWithPhoneNumberAndStatus
    organizationId: string
    appliedAppTemplateContainer: AppliedApplicationTemplateContainer
    facetContainer: ApplicationAnalyticsFacetContainer
    history: any
    applicationType: ApplicationType
    tooltip?: string
    disabled?: boolean
    hideEdit?: boolean
    hideFormButton?: boolean
    isInSidePanel?: boolean
    isFilterable?: boolean
    isSelected?: boolean
    onToggleFilter?: () => void
    isLoadingTemplates?: boolean
    startDate?: string
    endDate?: string
    interval?: AnalyticsInterval
    cueText?: string
    isCueHighlighted?: boolean
    onDismissCue?: () => void
}
export const formatPhoneNumber = (phoneNumber: string, virtual: boolean = false) => {
    if (virtual)
        return `${phoneNumber} (Virtual)`;
    // Remove any non-digit characters from the input number
    const cleanedNumber = phoneNumber?.replace(/\D/g, '');
    const formattedNumber = `+${cleanedNumber?.substring(0, 1)} (${cleanedNumber?.substring(1, 4)}) ${cleanedNumber?.substring(4, 7)}-${cleanedNumber?.substring(7)}`;
    return formattedNumber;
};

interface AppListItemProps {
    app?: ApplicationModelWithPhoneNumberAndStatus;
}

const ApplicationItem: React.FC<ApplicationItemProps> = (props) => {

    const trainingContainer = SparkDeploymentContainer.useContainer();
    const [copiedPhoneNumber, setCopiedPhoneNumber] = useState(false);
    const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (copyTimeoutRef.current !== null) {
                clearTimeout(copyTimeoutRef.current);
            }
        };
    }, []);

    const handleCopyPhoneNumber = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!navigator.clipboard?.writeText) {
            return;
        }
        const formattedPhoneNumber = props.app.virtualNumber
            ? props.app.virtualNumber
            : formatPhoneNumber(props.app.phoneNumber);
        if (formattedPhoneNumber) {
            navigator.clipboard.writeText(formattedPhoneNumber).then(() => {
                setCopiedPhoneNumber(true);
                if (copyTimeoutRef.current !== null) {
                    clearTimeout(copyTimeoutRef.current);
                }
                copyTimeoutRef.current = setTimeout(() => setCopiedPhoneNumber(false), 2000);
            });
        }
    };

    const renderTrainingState = () => {
        if (props.applicationType === "common") return null;

        switch (trainingContainer.getAppDeploymentState(props.app?.id)) {
            case "training":
                return (
                    <TrainingStatus className="publishing">Publishing</TrainingStatus>
                );
            case "trained":
                return (
                    <>
                        {
                            props.app.virtualNumber ?
                                <PhoneNumberLink to={`/v/apps/${props.app.id}`}>
                                    <PhoneNumber>{`${props.app.virtualNumber} (Virtual)`}</PhoneNumber>
                                </PhoneNumberLink>
                                :
                                props.app.phoneNumber ?
                                    <PhoneNumberLink to={`/v/apps/${props.app.id}`}>
                                        <PhoneNumber>{formatPhoneNumber(props.app.phoneNumber)}</PhoneNumber>
                                    </PhoneNumberLink>
                                    :
                                    null
                        }
                    </>
                );
            case "error":
                return (
                    <TooltipWrapper text="Try publishing again or contact support.">
                        <TrainingStatus className="error">Publish Error</TrainingStatus>
                    </TooltipWrapper>
                );
            default:
                return null;
        }
    };


    let analyticsQueryStr = "";
    if (props.startDate && props.endDate) {
        const startDate = moment(props.startDate).startOf('day').toISOString();
        const endDate = moment(props.endDate).endOf('day').toISOString();
        const interval = props.interval ?? 'custom';
        analyticsQueryStr = `?${new URLSearchParams({
            startDate,
            endDate,
            interval
        }).toString()}`;
    } else if (window?.location?.href) {
        const url = new URL(window?.location?.href);
        if (url.search) {
            const params = new URLSearchParams(url?.search);
            const year = moment().year();
            const month = params.get("month");

            const startDate = moment(`${month} 1, ${year}`, "MMMM D, YYYY");
            const endDate = startDate.clone().endOf('month');

            analyticsQueryStr = `?startDate=${startDate.format(DATE_FORMAT)}&endDate=${endDate.format(DATE_FORMAT)}&interval=month`;
        }
    }

    const appName = props.applicationType === "common"
        ? (props.app.name === aiAnsweringCommonAppName
            ? aiAnsweringCommonAppName
            : props.app.name === aiOrderingCommonAppName
                ? aiOrderingCommonAppName
                : aiDentalSchedulingCommonAppName)
        : props.app.name;

    // Get the applied templates specific to this app
    const appAppliedTemplates = props.appliedAppTemplateContainer?.state?.perApplicationAppliedTemplates?.find(
        t => t.applicationId === props.app.id
    );
    const hasAppTemplates = appAppliedTemplates?.appliedTemplates?.length > 0;

    const isComplete = !props.app.hasIncompleteFormFields && hasAppTemplates;
    const isLive = props.app.isLive ?? false;

    const isSharedConfig = props.applicationType === "common";
    const appLink = props.applicationType === "common"
        ? `/v/apps/${props.app.id}`
        : `/v/apps/${props.app.id}/analytics${analyticsQueryStr}`;

    // Check if app is currently publishing
    const isPublishing = props.applicationType !== "common" &&
        trainingContainer.getAppDeploymentState(props.app?.id) === "training";

    // Check if templates are still loading for this app
    const isLoadingStatus = props.isLoadingTemplates ||
        (props.appliedAppTemplateContainer?.state?.isLoading && !appAppliedTemplates);

    return (
        <>
            <ConditionalTooltipWrapper
                showTooltip={isPublishing}
                text="Hang tight! You'll be able to edit once published."
            >
                <AppListItem
                    app={props.app}
                    isInSidePanel={props.isInSidePanel}
                    isSharedConfig={isSharedConfig}
                    isSelected={props.isSelected}
                    isPublishing={isPublishing}
                    hasCue={!!props.cueText}
                    isCueHighlighted={props.isCueHighlighted}
                >
                    {props.isFilterable && (
                        <TooltipWrapper text={props.isSelected ? "Uncheck to exclude from charts" : "Check to include in charts"} replacementContentStyle={modernTooltipStyle} place="right">
                            <FilterCheckboxWrapper onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                props.onToggleFilter?.();
                            }}>
                                <FilterCheckbox
                                    type="checkbox"
                                    checked={props.isSelected}
                                    onChange={() => props.onToggleFilter?.()}
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label="Toggle filter for application"
                                />
                            </FilterCheckboxWrapper>
                        </TooltipWrapper>
                    )}
                    <StyledLink to={appLink}>
                        <AppContentWrapper>
                            <UserAvatar
                                firstName={appName?.split(' ')[0] || 'A'}
                                lastName={appName?.split(' ')[1] || 'P'}
                                size={32}
                                noBackground={false}
                                noShadow={true}
                            />
                            <AppNameWrapper>
                                <AssistantName>{appName}</AssistantName>
                                {(props.app.phoneNumber || props.app.virtualNumber) && (
                                    <PhoneNumberRow>
                                        <PhoneNumberText>
                                            {props.app.virtualNumber
                                                ? `${props.app.virtualNumber} (Virtual)`
                                                : formatPhoneNumber(props.app.phoneNumber)}
                                        </PhoneNumberText>
                                        <TooltipWrapper
                                            text={copiedPhoneNumber ? 'Copied!' : 'Copy phone number'}
                                            replacementContentStyle={modernTooltipStyle}
                                            place="top"
                                        >
                                            <CopyButton onClick={handleCopyPhoneNumber} copied={copiedPhoneNumber}>
                                                <CopyIcon src={copyIcon} />
                                            </CopyButton>
                                        </TooltipWrapper>
                                    </PhoneNumberRow>
                                )}
                            </AppNameWrapper>
                            {isPublishing ? (
                                <PublishingPill>
                                    <PublishingSpinner />
                                    Publishing
                                </PublishingPill>
                            ) : isLoadingStatus ? (
                                <StatusPillLoader>
                                    <StatusPillLoaderDot />
                                    <StatusPillLoaderDot />
                                    <StatusPillLoaderDot />
                                </StatusPillLoader>
                            ) : (
                                <StatusWrapper>
                                    {props.cueText && (
                                        <CueActions>
                                            <CueBadge
                                                role={props.onDismissCue ? 'button' : undefined}
                                                tabIndex={props.onDismissCue ? 0 : undefined}
                                                onClick={props.onDismissCue ? (e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    props.onDismissCue?.();
                                                } : undefined}
                                                onKeyDown={props.onDismissCue ? (e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        props.onDismissCue?.();
                                                    }
                                                } : undefined}
                                                aria-label={props.onDismissCue ? `Dismiss onboarding cue: ${props.cueText}` : undefined}
                                            >
                                                <CueText>{props.cueText}</CueText>
                                                {props.onDismissCue && <CueCloseMark aria-hidden="true">×</CueCloseMark>}
                                            </CueBadge>
                                        </CueActions>
                                    )}
                                    <TooltipWrapper text={isComplete ? 'Form complete' : 'Form needs review'} replacementContentStyle={modernTooltipStyle} place="top">
                                        <FormStatusDot isComplete={isComplete} />
                                    </TooltipWrapper>
                                    {!isSharedConfig && (
                                        <LiveStatusPill isLive={isLive}>
                                            {isLive ? 'Live' : 'Not Live'}
                                        </LiveStatusPill>
                                    )}
                                </StatusWrapper>
                            )}
                        </AppContentWrapper>
                    </StyledLink>
                </AppListItem>
            </ConditionalTooltipWrapper>
            {/* {
                props.disabled || (trainingContainer.getAppDeploymentState(props.app?.id) === "training") ?
                    <TooltipWrapper text={props.disabled ? "You must fill out shared topics forms before you can edit location forms." : "Hang tight! You'll be able to edit once published."}>
                        <DisabledAppListItem isInSidePanel={props.isInSidePanel}>
                            <AppListItemSide>
                                <AssistantIcon src={props.applicationType === "common" ? sharedImage : assistantImage} />
                                <AssistantName>{props.applicationType === "common" ? (props.app.name === aiDentalSchedulingCommonAppName ? aiDentalSchedulingCommonAppName : aiAnsweringCommonAppName) : props.app.name}</AssistantName>
                            </AppListItemSide>
                            <AppListItemSide>
                                {renderTrainingState()}
                                {
                                    !props.hideFormButton && props.app.hasIncompleteFormFields === true &&
                                    <ConditionalTooltipWrapper showTooltip={!!props.tooltip?.length} text={props.tooltip}>
                                        <FormIncompleteWrapper>
                                            <IncompleteText>Form Incomplete</IncompleteText>
                                            <ActionIcon src={formIncompleteImage} />
                                        </FormIncompleteWrapper>
                                    </ConditionalTooltipWrapper>
                                }
                                {
                                    !props.hideFormButton &&
                                    props.app.hasIncompleteFormFields === false &&
                                    props.appliedAppTemplateContainer?.state?.appliedTemplates?.length > 0 &&
                                    <ConditionalTooltipWrapper showTooltip={!!props.tooltip?.length} text={props.tooltip}>
                                        <ActionIcon src={formCompleteImage} />
                                    </ConditionalTooltipWrapper>
                                }
                                {
                                    props.applicationType === "location" &&
                                    <ActionIcon src={analyticsIcon} />
                                }
                                {
                                    !props.hideEdit &&
                                    <ActionIcon src={editImage} />
                                }
                            </AppListItemSide>
                        </DisabledAppListItem>
                    </TooltipWrapper>
                    :
                    <AppListItem app={props.app} isInSidePanel={props.isInSidePanel}>
                        <StyledLink to={`/v/apps/${props.app.id}`}>
                            <AppListItemSide>
                                <AssistantIcon src={props.applicationType === "common" ? sharedImage : assistantImage} />
                                <AssistantName>{props.applicationType === "common" ? (props.app.name === aiDentalSchedulingCommonAppName ? aiDentalSchedulingCommonAppName : aiAnsweringCommonAppName) : props.app.name}</AssistantName>
                            </AppListItemSide>
                        </StyledLink>
                        <StyledLink to={`/v/apps/${props.app.id}`}>
                            <AppListItemSide>
                                {renderTrainingState()}
                                {
                                    !props.hideFormButton && props.app.hasIncompleteFormFields === true &&
                                    <ConditionalTooltipWrapper showTooltip={!!props.tooltip?.length} text={props.tooltip}>
                                        <Link to={`/v/apps/${props.app?.id}`}>
                                            <FormIncompleteWrapper>
                                                <IncompleteText>Form Incomplete</IncompleteText>
                                                <ActionIcon src={formIncompleteImage} />
                                            </FormIncompleteWrapper>
                                        </Link>
                                    </ConditionalTooltipWrapper>
                                }
                                {
                                    !props.hideFormButton &&
                                    props.app.hasIncompleteFormFields === false &&
                                    props.appliedAppTemplateContainer?.state?.appliedTemplates?.length > 0 &&
                                    <ConditionalTooltipWrapper showTooltip={!!props.tooltip?.length} text={props.tooltip}>
                                        <Link to={`/v/apps/${props.app?.id}`}>
                                            <ActionIcon src={formCompleteImage} />
                                        </Link>
                                    </ConditionalTooltipWrapper>
                                }
                                {
                                    props.applicationType === "location" &&
                                    <Link to={`/v/apps/${props.app.id}/analytics${analyticsQueryStr}`}>
                                        <ActionIcon src={analyticsIcon} />
                                    </Link>
                                }
                                {
                                    !props.hideEdit &&
                                    <Link to={`/v/apps/${props.app?.id}`}>
                                        <ActionIcon src={editImage} />
                                    </Link>
                                }
                            </AppListItemSide>
                        </StyledLink>
                    </AppListItem>

            } */}
        </>
    );

};

const modernTooltipStyle = css`
  .rc-tooltip-inner {
    font-family: Muli, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.3px;
    line-height: 1.4;
    z-index: 120;
    color: white;
    background: linear-gradient(135deg, ${voicify_spark_darker} 0%, #1a4d6b 100%);
    box-shadow: 0 4px 12px rgba(30, 126, 185, 0.25), 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    padding: 6px 10px;
    min-height: 0;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  .rc-tooltip-arrow {
    display: none;
  }
`;

const AppContentWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-width: 0;
    overflow: hidden;
`;

const AppNameWrapper = styled.div`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const AssistantName = styled.p`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 11px;
    font-weight: 600;
    margin: 0;
    color: ${color_shades_darkest};
    ${breakpoint_macbook_small} {
        font-size: 10px;
    }
    ${breakpoint_large} {
        font-size: 9px;
    }
    ${breakpoint_small} {
        font-size: 10px;
    }
`;

const PhoneNumberText = styled.p`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 9px;
    font-weight: 500;
    margin: 0;
    color: ${color_shades_darker};
    ${breakpoint_macbook_small} {
        font-size: 8px;
    }
    ${breakpoint_large} {
        font-size: 7px;
    }
    ${breakpoint_small} {
        font-size: 8px;
    }
`;

const PhoneNumberRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
`;

const CopyButton = styled.button<{ copied?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.copied ? '#E8F5E9' : 'transparent'};
    border: none;
    border-radius: 4px;
    padding: 2px;
    cursor: pointer;
    opacity: ${props => props.copied ? 1 : 0.6};
    transition: all 0.2s;
    flex-shrink: 0;
    
    &:hover {
        opacity: 1;
        background: ${color_shades_light};
    }
`;

const CopyIcon = styled.img`
    width: 12px;
    height: 12px;
`;

const StatusWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;

const FormStatusDot = styled.div<{ isComplete: boolean }>`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${props => props.isComplete ? color_colors_growth : '#E5B94C'};
    opacity: ${props => props.isComplete ? 1 : 0.6};
    flex-shrink: 0;
    cursor: help;
    transition: all 0.2s;
    
    &:hover {
        transform: scale(1.3);
        opacity: 1;
    }
`;

const LiveStatusPill = styled.div<{ isLive: boolean }>`
    padding: 4px 10px;
    border-radius: 16px;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
    background-color: ${props => props.isLive ? '#E8F5E9' : color_shades_light};
    color: ${props => props.isLive ? color_colors_growth : color_shades_darker};
    border: 1px solid ${props => props.isLive ? '#A5D6A7' : color_shades_light};
    transition: all 0.2s;
    flex-shrink: 0;

    ${breakpoint_macbook_small} {
        font-size: 9px;
        padding: 3px 8px;
    }
    ${breakpoint_large} {
        font-size: 8px;
        padding: 2px 6px;
    }
    ${breakpoint_small} {
        font-size: 9px;
        padding: 3px 8px;
    }
`;

const StatusPillLoader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    padding: 4px 14px;
    border-radius: 16px;
    background-color: ${color_shades_light};
    border: 1px solid ${color_shades_light};
    flex-shrink: 0;
    min-width: 70px;
    
    ${breakpoint_small} {
        padding: 3px 10px;
        min-width: 60px;
    }
`;

const StatusPillLoaderDot = styled.div`
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: ${color_shades_darker};
    animation: statusPulse 1.2s ease-in-out infinite;
    
    &:nth-of-type(2) {
        animation-delay: 0.2s;
    }
    
    &:nth-of-type(3) {
        animation-delay: 0.4s;
    }
    
    @keyframes statusPulse {
        0%, 80%, 100% {
            opacity: 0.3;
            transform: scale(0.8);
        }
        40% {
            opacity: 1;
            transform: scale(1);
        }
    }
`;

const PublishingPill = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 16px;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
    background-color: #E8F5E9;
    color: ${color_colors_growth};
    border: 1px solid #A5D6A7;
    flex-shrink: 0;
    
    ${breakpoint_small} {
        font-size: 9px;
        padding: 3px 8px;
        gap: 4px;
    }
`;

const PublishingSpinner = styled.div`
    width: 10px;
    height: 10px;
    border: 2px solid #A5D6A7;
    border-top-color: ${color_colors_growth};
    border-radius: 50%;
    animation: publishingSpin 0.8s linear infinite;
    
    @keyframes publishingSpin {
        to {
            transform: rotate(360deg);
        }
    }
    
    ${breakpoint_small} {
        width: 8px;
        height: 8px;
        border-width: 1.5px;
    }
`;

const StyledLink = styled(Link)`
    text-decoration: none;
    color: inherit;
    flex: 1;
    min-width: 0;
    overflow: hidden;
`;

const PhoneNumberLink = styled(Link)`
    text-decoration: none;
    color: inherit;
`;

const TrainingStatus = styled.div`
    font-size: 10px;
    font-weight: 600;
    margin-right: 12px;
    &.publishing {
        color: ${color_colors_growth};
        position: relative;
    }
    &.publishing:after {
        content: '';
        animation: dots 1.5s steps(1, end) infinite;
        position: absolute;
        margin-left: 2px;
    }
    &.published {
        color: ${color_colors_growth};
    }
    &.error {
        color: ${color_colors_decline};
    }

    @keyframes dots {
        0%, 20% {
            content: '.';
        }
        40% {
            content: '..';
        }
        60%, 80% {
            content: '...';
        }
        100% {
            content: '';
        }
    }
    ${breakpoint_small} {
        display: none;
    }
`;

const FormIncompleteWrapper = styled.div`
    display: flex;
    align-items: center;
`;

interface AppListItemStyledProps extends AppListItemProps {
    isInSidePanel?: boolean;
    isSharedConfig?: boolean;
    isSelected?: boolean;
    isPublishing?: boolean;
    hasCue?: boolean;
    isCueHighlighted?: boolean;
}

const FilterCheckboxWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 8px 0 4px;
    cursor: pointer;
    z-index: 10;
    pointer-events: auto;
`;

const FilterCheckbox = styled.input`
    width: 16px;
    height: 16px;
    cursor: pointer;
    appearance: none;
    border: 2px solid ${color_shades_light};
    border-radius: 3px;
    transition: all 0.2s;
    background-color: ${color_defaults_white};
    position: relative;
    
    &:checked {
        background-color: ${voicify_spark_darker};
        border-color: ${voicify_spark_darker};
    }
    
    &:checked::after {
        content: '';
        position: absolute;
        left: 50%;
        top: 45%;
        width: 3px;
        height: 7px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: translate(-50%, -50%) rotate(45deg);
    }
    
    &:hover {
        transform: scale(1.1);
        border-color: ${voicify_spark_medium};
    }
`;

const AppListItem = styled.div<AppListItemStyledProps>`
    text-decoration: none;
    color: ${dark_grey_blue};
    background-color: ${props => props.isPublishing ? color_shades_light : props.isSharedConfig ? voicify_spark_lightest : color_defaults_white};
    cursor: ${props => props.isPublishing ? 'not-allowed' : 'pointer'};
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
    border: 1px solid ${props => props.isCueHighlighted ? 'rgba(27, 165, 220, 0.42)' : props.isPublishing ? color_shades_light : props.isSharedConfig ? 'rgba(27, 165, 220, 0.25)' : color_shades_light};
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 12px;
    transition: all 0.3s ease;
    box-shadow: ${props => props.isCueHighlighted
        ? '0 6px 18px rgba(30, 126, 185, 0.14), 0 0 0 1px rgba(27, 165, 220, 0.08)'
        : '0 2px 4px rgba(50, 70, 97, 0.08)'};
    position: relative;
    overflow: hidden;
    opacity: ${props => props.isPublishing ? 0.7 : props.isSelected === false ? 0.4 : 1};
    pointer-events: ${props => props.isPublishing ? 'none' : 'auto'};
    animation: ${props => props.isCueHighlighted ? 'cueGlow 2.6s ease-in-out infinite' : 'none'};

    @keyframes cueGlow {
        0%, 100% {
            border-color: rgba(27, 165, 220, 0.28);
            box-shadow: 0 4px 12px rgba(30, 126, 185, 0.1), 0 0 0 0 rgba(27, 165, 220, 0);
        }
        50% {
            border-color: rgba(27, 165, 220, 0.46);
            box-shadow: 0 8px 20px rgba(30, 126, 185, 0.16), 0 0 0 6px rgba(27, 165, 220, 0.08);
        }
    }
    
    ${props => props.isSharedConfig && !props.isPublishing && `
        &:before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 3px;
            background: linear-gradient(180deg, ${voicify_spark_medium} 0%, ${voicify_spark_darker} 100%);
        }
    `}
    
    &:hover {
        transform: ${props => props.isPublishing ? 'none' : 'translateY(-2px)'};
        box-shadow: ${props => props.isPublishing ? '0 2px 4px rgba(50, 70, 97, 0.08)' : '0 4px 12px rgba(30, 126, 185, 0.15)'};
        border-color: ${props => props.isPublishing ? color_shades_light : props.isSharedConfig ? voicify_spark_darker : voicify_spark_medium};
        opacity: ${props => props.isPublishing ? 0.7 : props.isSelected === false ? 0.6 : 1};
    }
    
    ${breakpoint_small} {
        padding: 12px;
        margin-bottom: 8px;
    }
`;

const DisabledAppListItem = styled.div<{ isInSidePanel?: boolean }>`
    text-decoration: none;
    color: ${color_shades_darker};
    background-color: ${color_shades_light};
    cursor: not-allowed;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: ${style_border_default};
    border-top: ${props => props.isInSidePanel ? style_border_default : 'none'};
    border-radius: 6px;
    height: 44px;
    padding: 0 12px;
    opacity: 0.7;
    ${breakpoint_small} {
        font-size: 12px;
        padding: 0 8px;
        height: 48px;
    }
`;

const AssistantIcon = styled.img`
    margin-right: ${props => props.src === sharedImage ? '10px' : '8px'};
    width: ${props => props.src === sharedImage ? '18px' : '20px'};
    height: ${props => props.src === sharedImage ? '18px' : '20px'};
    ${breakpoint_small} {
        display: none;
    }
`;

const AppListItemSide = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
`;

const ActionIcon = styled.img<{ isInSidePanel?: boolean }>`
    margin: 0 0 0 8px;
    width: 16px;
    height: 16px;
    opacity: 0.8;
    transition: opacity 0.2s;
    
    &:hover {
        opacity: 1;
    }
    
    ${breakpoint_small} {
        margin: 0 4px;
        height: 16px;
        width: 16px;
    }
`;

const IncompleteText = styled.div`
    font-size: 9px;
    margin: 0 4px 0 0;
    color: ${color_colors_decline};
    font-weight: 600;
    ${breakpoint_small} {
        display: none;
    }
`;

const PhoneNumber = styled.div`
    margin-right: 12px;
    font-size: 11px;
    color: #6b7280;
    font-weight: 500;
    ${breakpoint_small} {
        font-size: 9px;
    }
`;

const CueBadge = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 6px 10px 6px 12px;
    border-radius: 999px;
    background: ${color_variants_gold_light};
    border: 1px solid ${color_colors_gold};
    box-shadow: ${premium_gold_shadow};
    backdrop-filter: blur(8px);
    white-space: nowrap;
    transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        background: rgba(246, 161, 47, 0.14);
        transform: translateY(-1px);
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(212, 160, 23, 0.18), ${premium_gold_shadow};
    }

    ${breakpoint_small} {
        padding: 5px 10px;
    }
`;

const CueText = styled.span`
    color: ${premium_gold};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.01em;

    ${breakpoint_small} {
        font-size: 9px;
    }
`;

const CueCloseMark = styled.span`
    color: ${premium_gold};
    font-size: 13px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -1px;
`;

const CueActions = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
`;

export default ApplicationItem;
